/**
 * Surgeon role conflict validation — shared by the portal's submit and edit
 * claim forms (spec 028 FR-027).
 *
 * Rules:
 *  - Primary Surgeon and Assistant Surgeon cannot be selected with any
 *    co-surgeon option (One Co-Surgeon, Both Co-Surgeons, individual
 *    CO_SURGEON_1 / CO_SURGEON_2).
 *  - One Co-Surgeon and Both Co-Surgeons (or its individual sub-roles) are
 *    mutually exclusive.
 *
 * Returns a human-readable error string when a conflict is detected, or
 * `null` when the role mix is valid.
 */

const PRIMARY_ASSISTANT_ROLES = ['PRIMARY_SURGEON', 'ASSISTANT_SURGEON'] as const;
const CO_SURGEON_ROLES = [
  'ONE_CO_SURGEON',
  'BOTH_CO_SURGEONS',
  'CO_SURGEON_1',
  'CO_SURGEON_2',
] as const;

export function getSurgeonConflictError(roles: string[]): string | null {
  const hasPrimaryOrAssistant = roles.some((r) =>
    PRIMARY_ASSISTANT_ROLES.includes(r as typeof PRIMARY_ASSISTANT_ROLES[number]),
  );
  const hasCoSurgeon = roles.some((r) =>
    CO_SURGEON_ROLES.includes(r as typeof CO_SURGEON_ROLES[number]),
  );
  const hasOneCoSurgeon = roles.includes('ONE_CO_SURGEON');
  const hasBothCoSurgeons = roles.some(
    (r) => r === 'CO_SURGEON_1' || r === 'CO_SURGEON_2' || r === 'BOTH_CO_SURGEONS',
  );

  if (hasPrimaryOrAssistant && hasCoSurgeon) {
    return 'Primary/Assistant Surgeon and Co-Surgeon options cannot be billed together. Please select one or the other.';
  }
  if (hasOneCoSurgeon && hasBothCoSurgeons) {
    return 'One Co-Surgeon and Both Co-Surgeons cannot be selected together. Please choose one option.';
  }
  return null;
}
